import { createRouter, createWebHistory } from 'vue-router'

import { UserRole } from '@/enums/admin'
import { getUserRole, isAuthenticated } from '@/libs/auth'
import { useAppStore } from '@/stores/app'

// Roles allowed on the full admin surface (everything except the restricted
// instalater role). Routes without meta.roles are open to any authenticated user.
const ADMIN_VIEWER = [UserRole.Admin, UserRole.Viewer]

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition
        if (from.path === to.path) return {}
        return { top: 0, behavior: 'smooth' }
    },
    routes: [
        { path: '/', redirect: '/dashboard' },
        {
            path:      '/login',
            name:      'login',
            component: () => import('@/views/Login.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Public self-service free-tier signup form (no auth).
            path:      '/signup',
            name:      'signup',
            component: () => import('@/views/signup/Signup.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Public signup confirmation (e-mail verification link target, no auth).
            path:      '/signup/confirm',
            name:      'signup-confirm',
            component: () => import('@/views/signup/SignupConfirm.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            // Public VM-image download page (no auth): the four appliance formats,
            // served statically by admin-web nginx from /downloads/vm/.
            path:      '/downloads',
            name:      'downloads',
            component: () => import('@/views/downloads/Downloads.vue'),
            meta:      { layout: 'Blank', requiresAuth: false },
        },
        {
            path:      '/dashboard',
            name:      'dashboard',
            component: () => import('@/views/dashboard/Dashboard.vue'),
            meta:      { requiresAuth: true, roles: ADMIN_VIEWER },
        },
        {
            path:      '/stranke',
            name:      'customers',
            component: () => import('@/views/customers/Customers.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/instance',
            name:      'instances',
            component: () => import('@/views/instances/Instances.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/instance/:id',
            name:      'instance',
            component: () => import('@/views/instances/Instance.vue'),
            meta:      { requiresAuth: true },
        },
        {
            path:      '/paketi',
            name:      'plans',
            component: () => import('@/views/plans/Plans.vue'),
            meta:      { requiresAuth: true, roles: ADMIN_VIEWER },
        },
        {
            path:      '/izdaje',
            name:      'releases',
            component: () => import('@/views/releases/Releases.vue'),
            meta:      { requiresAuth: true, roles: ADMIN_VIEWER },
        },
        {
            path:      '/obracun',
            name:      'billing',
            component: () => import('@/views/billing/Billing.vue'),
            meta:      { requiresAuth: true, roles: ADMIN_VIEWER },
        },
        {
            path:      '/uporabniki',
            name:      'users',
            component: () => import('@/views/users/Users.vue'),
            meta:      { requiresAuth: true, roles: ADMIN_VIEWER },
        },
        {
            // Mailcow mailbox management ("E-predali") — admin only.
            path:      '/e-predali',
            name:      'mailboxes',
            component: () => import('@/views/mailboxes/Mailboxes.vue'),
            meta:      { requiresAuth: true, roles: [UserRole.Admin] },
        },
        {
            // Console mail client ("Pošta") for info@/support@ — admin only.
            path:      '/posta',
            name:      'mail',
            component: () => import('@/views/mail/Mail.vue'),
            meta:      { requiresAuth: true, roles: [UserRole.Admin] },
        },
        {
            // Support ticketing ("Podpora") around support@ — admin only.
            path:      '/podpora',
            name:      'support',
            component: () => import('@/views/support/Support.vue'),
            meta:      { requiresAuth: true, roles: [UserRole.Admin] },
        },
        {
            // "Oddaljen dostop" — remote-support sessions into customer
            // appliances. Deliberately NOT the same page as /podpora, which is
            // the support@ ticket queue: one is correspondence, this one is a
            // door into somebody else's estate, and they should never be
            // approved with the same click. Readable by every console role;
            // approving, refusing and ending are Admin, enforced server-side.
            path:      '/oddaljen-dostop',
            name:      'remote-access',
            component: () => import('@/views/remote/RemoteAccess.vue'),
            meta:      { requiresAuth: true },
        },
        {
            // Settings (SMTP + global mail redirect) — admin only.
            path:      '/nastavitve',
            name:      'settings',
            component: () => import('@/views/settings/Settings.vue'),
            meta:      { requiresAuth: true, roles: [UserRole.Admin] },
        },
        {
            // Help/guide — open to every authenticated role (incl. instalater).
            path:      '/pomoc',
            name:      'help',
            component: () => import('@/views/help/Help.vue'),
            meta:      { requiresAuth: true },
        },
    ],
})

// homeRouteFor returns the landing route for a role: the instalater role cannot
// see the dashboard, so it lands on the instances list.
const homeRouteFor = (role: string) =>
    role === UserRole.Instalater ? { name: 'instances' } : { name: 'dashboard' }

// Roles the guard below knows how to route. A token carrying anything else (or
// no role claim at all) cannot be routed by role: homeRouteFor() would answer
// 'dashboard', whose own role check would reject it again and redirect to
// 'dashboard' — an endless redirect. vue-router only breaks that recursion in a
// DEV build, so in production such a session would lock the tab up. An
// unroutable session stops at the login screen instead.
const ROUTABLE_ROLES: string[] = [UserRole.Admin, UserRole.Viewer, UserRole.Instalater]

router.beforeEach((to, from, next) => {
    const appStore = useAppStore()
    const loggedIn = isAuthenticated()
    const role     = getUserRole()
    const routable = loggedIn && ROUTABLE_ROLES.includes(role)

    if (routable && to.name === 'login') return next(homeRouteFor(role))
    if (to.meta.requiresAuth && !loggedIn) {
        appStore.setLogout()
        return next({ name: 'login' })
    }
    if (to.meta.requiresAuth && !routable) return next({ name: 'login' })

    // Role-restricted routes: send a caller without the required role to their home.
    const roles = to.meta.roles as string[] | undefined
    if (routable && roles && !roles.includes(role)) {
        const home = homeRouteFor(role)
        // Never bounce a route onto itself, whatever the role table says.
        return home.name === to.name ? next() : next(home)
    }

    next()
})

// Loader hygiene: an action that threw between its startLoading() and its
// stopLoading() would leave loaderActive > 0 and keep a submit button spinning
// (and would block clicks outright if a full-screen overlay is ever wired to this
// counter). Clearing it on every completed navigation makes that unrecoverable
// state impossible.
router.afterEach(() => {
    useAppStore().resetLoading()
})

export default router
